import type { AgentReport } from './AgentResult';
import { AgentTests } from './AgentTests';
import { AgentCoherence } from './AgentCoherence';
import { AgentAnomalies } from './AgentAnomalies';
import type { Bulletin } from '@shared/types/Bulletin';
import type { Ecriture } from '@shared/types/Ecriture';
import type { Salarie } from '@shared/types/Salarie';

export type Certificate = 'PROD-READY' | 'STAGING-READY' | 'NOT-READY';

export interface OrchestratorResult {
  reports: AgentReport[];
  scoreTotal: number;
  scoreMax: number;
  scorePct: number;
  failsCritiques: number;
  certificate: Certificate;
  generatedAt: string;
}

/**
 * Orchestrateur du Centre Qualité.
 * Score pondéré : Tests (4), Cohérence (3), Anomalies (2).
 * Certificat :
 * - PROD-READY si 0 fail critique et score >= 95%
 * - STAGING-READY si 0 fail critique et score 85-94%
 * - NOT-READY sinon
 */
export class Orchestrator {
  private static readonly WEIGHTS = { tests: 4, coherence: 3, anomalies: 2 } as const;

  static run(
    salaries: readonly Salarie[],
    bulletins: readonly Bulletin[],
    ecritures: readonly Ecriture[],
  ): OrchestratorResult {
    const reports = [
      AgentTests.run(),
      AgentCoherence.run(bulletins, ecritures),
      AgentAnomalies.run(salaries, bulletins),
    ];

    const weights = [this.WEIGHTS.tests, this.WEIGHTS.coherence, this.WEIGHTS.anomalies];
    let scoreTotal = 0,
      scoreMax = 0,
      failsCritiques = 0;

    reports.forEach((report, idx) => {
      const weight = weights[idx]!;
      const ok = report.results.filter((r) => r.level === 'ok').length;
      const warn = report.results.filter((r) => r.level === 'warn').length;
      const fails = report.results.filter((r) => r.level === 'fail');
      if (idx <= 1) failsCritiques += fails.length; // tests + coherence = critiques

      scoreTotal += (ok + warn * 0.5) * weight;
      scoreMax += report.results.length * weight;
    });

    const scorePct = scoreMax > 0 ? Math.round((scoreTotal / scoreMax) * 100) : 0;
    let certificate: Certificate;
    if (failsCritiques === 0 && scorePct >= 95) certificate = 'PROD-READY';
    else if (failsCritiques === 0 && scorePct >= 85) certificate = 'STAGING-READY';
    else certificate = 'NOT-READY';

    return {
      reports,
      scoreTotal,
      scoreMax,
      scorePct,
      failsCritiques,
      certificate,
      generatedAt: new Date().toISOString(),
    };
  }
}
